import React, { FC } from 'react';
import classNames from 'classnames';
import Utils from '../utils';
import { LoanFormatIcon } from '../common/GildIcon';
import { useTranslation } from 'react-i18next';
import { SpocState } from '../../app-store/authentication/authenticationReducer';

type Props = {
    readonly partner: SpocState;
    readonly activeSpoc?: SpocState;
    readonly isAdmin?: boolean;
    readonly isSinglePartner?: boolean;
    readonly showDropdown?: boolean;
    readonly changeSpoc: (spoc: SpocState) => void;
    readonly openSpoc: (spoc: SpocState) => void;
};

export const SiteSelector: FC<Props> = props => {
    const { t } = useTranslation();
    const { partner, isSinglePartner, showDropdown } = props;

    function changeSpoc(spoc: SpocState) {
        if (!isActiveSpoc(spoc) && spoc.selectable) {
            props.changeSpoc(spoc);
        }
    }

    const isActiveSpoc = (spoc: SpocState) => spoc.id === props.activeSpoc?.id;

    const addSpaceInSpocId = (spocId: string) =>
        spocId.startsWith('PARTNER')
            ? `${spocId.substring(0, 7)} ${spocId.substring(7)}`
            : `${spocId.substring(0, 4)} ${spocId.substring(4)}`;

    return (
        <li className={classNames({ active: showDropdown || isSinglePartner })}>
            {partner.id !== null ? (
                <div className="partner">
                    {!isSinglePartner ? (
                        <a
                            className="partner-expand-icon"
                            onClick={() => props.openSpoc(partner)}
                            data-close-dropdown="false"
                        >
                            <i className="material-icons">expand_more</i>
                        </a>
                    ) : null}
                    <a
                        className={classNames('details', { active: isActiveSpoc(partner) })}
                        onClick={() => changeSpoc(partner)}
                    >
                        <div className="spoc-info-line">
                            <span>{partner.name}</span>
                            {props.isAdmin ? (
                                <span className="marked light-grey">{partner.distributionTerritory}</span>
                            ) : null}
                            {partner.eaudiobooksStockAmount ? (
                                <span className="marker-badge display-inline-flex">
                                    <LoanFormatIcon icon="eaudiobook-filled" />
                                    <span>
                                        {' '}
                                        {t('numberFormat.numberWithoutDecimalPoint', {
                                            val: partner.eaudiobooksStockAmount
                                        })}
                                    </span>
                                </span>
                            ) : null}
                            {partner.ebooksStockAmount ? (
                                <span className="marker-badge display-inline-flex">
                                    <LoanFormatIcon icon="ebook-filled" />
                                    <span>
                                        {' '}
                                        {t('numberFormat.numberWithoutDecimalPoint', {
                                            val: partner.ebooksStockAmount
                                        })}
                                    </span>
                                </span>
                            ) : null}
                            {partner.epressStockAmount ? (
                                <span className="marker-badge display-inline-flex">
                                    <LoanFormatIcon icon="emagazine-filled" />
                                    <span>
                                        {' '}
                                        {t('numberFormat.numberWithoutDecimalPoint', {
                                            val: partner.epressStockAmount
                                        })}
                                    </span>
                                </span>
                            ) : null}
                            {partner.stockAmount && props.isAdmin ? (
                                <span className="marker-badge display-inline-flex">
                                    <LoanFormatIcon icon="star-outline" />
                                    <span>
                                        {' '}
                                        {t('numberFormat.numberWithoutDecimalPoint', { val: partner.stockAmount })}
                                    </span>
                                </span>
                            ) : null}
                        </div>
                        <div className="small">
                            {isSinglePartner ? (
                                <i className="material-icons sharedCollection fl-left">share&nbsp;</i>
                            ) : null}
                            {props.isAdmin
                                ? t('sharedCollectionWithSpocId', {
                                      spocId: Utils.toTitleCase(addSpaceInSpocId(partner.id))
                                  })
                                : t('sharedCollection')}
                        </div>
                        <i className="material-icons checked">check</i>
                    </a>
                </div>
            ) : null}
            <ul className="sites">
                {[...partner.sites]
                    .sort((a, b) => (a.name !== b.name ? a.name.localeCompare(b.name) : a.id.localeCompare(b.id))) // sort by name, then by id
                    .map(site => (
                        <li key={site.id}>
                            <a
                                className={classNames('details', { active: isActiveSpoc(site) })}
                                onClick={() => changeSpoc(site)}
                            >
                                <div className="spoc-info-line">
                                    <span>{site.name}</span>
                                    {props.isAdmin ? (
                                        <span className="marked light-grey">{site.distributionTerritory}</span>
                                    ) : null}
                                    {site.eaudiobooksStockAmount ? (
                                        <span className="marker-badge display-inline-flex">
                                            <LoanFormatIcon icon="eaudiobook-filled" />
                                            <span>
                                                {' '}
                                                {t('numberFormat.numberWithoutDecimalPoint', {
                                                    val: site.eaudiobooksStockAmount
                                                })}
                                            </span>
                                        </span>
                                    ) : null}
                                    {site.ebooksStockAmount ? (
                                        <span className="marker-badge display-inline-flex">
                                            <LoanFormatIcon icon="ebook-filled" />
                                            <span>
                                                {' '}
                                                {t('numberFormat.numberWithoutDecimalPoint', {
                                                    val: site.ebooksStockAmount
                                                })}
                                            </span>
                                        </span>
                                    ) : null}
                                    {site.epressStockAmount ? (
                                        <span className="marker-badge display-inline-flex">
                                            <LoanFormatIcon icon="emagazine-filled" />
                                            <span>
                                                {' '}
                                                {t('numberFormat.numberWithoutDecimalPoint', {
                                                    val: site.epressStockAmount
                                                })}
                                            </span>
                                        </span>
                                    ) : null}
                                    {site.stockAmount && props.isAdmin ? (
                                        <span className="marker-badge display-inline-flex">
                                            <LoanFormatIcon icon="star-outline" />
                                            <span>
                                                {' '}
                                                {t('numberFormat.numberWithoutDecimalPoint', {
                                                    val: site.stockAmount
                                                })}
                                            </span>
                                        </span>
                                    ) : null}
                                </div>
                                <div className="small">
                                    {props.isAdmin
                                        ? t('individualCollectionWithSpocId', {
                                              spocId: Utils.toTitleCase(addSpaceInSpocId(site.id))
                                          })
                                        : t('individualCollection')}
                                </div>
                                <i className="material-icons checked">check</i>
                            </a>
                        </li>
                    ))}
            </ul>
        </li>
    );
};
